import {
    Button,
    Group,
    Paper,
    SegmentedControl,
    Table,
    Text,
    Title,
    ActionIcon,
    Tooltip
} from "@mantine/core";
import React, { useMemo, useState } from "react";
import type { VisaApplication } from "../../types/VisaApplication";
import { IconPencil, IconTrash } from "@tabler/icons-react";

const formatDate = (value: string | null | undefined) => {
    if (!value) return "";
    return new Date(value).toLocaleDateString();
};

type Props = {
    title: string;
    data: VisaApplication[];
    mode: "admin" | "user";
    onEdit?: (row: VisaApplication) => void;
    onDelete?: (id: number) => void;
    showStatusInUser?: boolean;
};

export default function VisaTable({
    title,
    data,
    mode,
    onEdit,
    onDelete,
    showStatusInUser = true,
}: Props) {
    const apiUrl = process.env.REACT_APP_API_URL ?? "";
    const isAdmin = mode === "admin";
    const showStatus = isAdmin || showStatusInUser;

    const [statusFilter, setStatusFilter] = useState<"all" | "notSent" | "sent">("all");

    const filteredData = useMemo(() => {
        if (statusFilter === "sent") return data.filter((x) => !!x.sent);
        if (statusFilter === "notSent") return data.filter((x) => !x.sent);
        return data;
    }, [data, statusFilter]);

    const getInvitationFileUrl = (fileValue?: string | null) => {
        const raw = fileValue?.trim();
        if (!raw) return null;

        if (raw.startsWith("http://") || raw.startsWith("https://")) {
            return raw;
        }

        const normalizedFileName = raw.split("\\").pop()?.split("/").pop()?.trim();
        if (!normalizedFileName) return null;

        return `${apiUrl}/uploads/visa/${encodeURIComponent(normalizedFileName)}`;
    };

    return (
        <Paper withBorder radius="md" shadow="sm" p="sm" style={{ width: "min(1400px, 98vw)" }}>
            <Group justify="space-between" mb="sm" align="center">
                <Title order={3}>{title}</Title>

                <Group gap="sm">
                    <SegmentedControl
                        value={statusFilter}
                        onChange={(v) => setStatusFilter(v as "all" | "notSent" | "sent")}
                        data={[
                            { label: "All", value: "all" },
                            { label: "Not sent", value: "notSent" },
                            { label: "Sent", value: "sent" },
                        ]}
                    />
                    <Text size="sm">Count: {filteredData.length}</Text>
                </Group>
            </Group>

            <Table.ScrollContainer minWidth={1200}>
                <Table
                    striped="even"
                    highlightOnHover
                    verticalSpacing={4}
                    horizontalSpacing="xs"
                    withTableBorder={false}
                    withColumnBorders={false}
                >
                    <Table.Thead
                        style={{
                            backgroundColor: "var(--mantine-color-gray-0)",
                            borderBottom: "1px solid var(--mantine-color-gray-3)",
                        }}
                    >
                        <Table.Tr>
                            <Table.Th style={{ whiteSpace: "nowrap" }}>Firstname</Table.Th>
                            <Table.Th style={{ whiteSpace: "nowrap" }}>Middlename</Table.Th>
                            <Table.Th style={{ whiteSpace: "nowrap" }}>Lastname</Table.Th>
                            <Table.Th style={{ whiteSpace: "nowrap" }}>Date of birth</Table.Th>
                            <Table.Th style={{ whiteSpace: "nowrap" }}>Expiry date</Table.Th>
                            <Table.Th style={{ whiteSpace: "nowrap" }}>Issue date</Table.Th>
                            <Table.Th style={{ whiteSpace: "nowrap" }}>Nationality</Table.Th>
                            <Table.Th style={{ whiteSpace: "nowrap" }}>Passport</Table.Th>
                            <Table.Th style={{ whiteSpace: "nowrap" }}>Place of birth</Table.Th>
                            <Table.Th style={{ whiteSpace: "nowrap" }}>City to apply</Table.Th>

                            {isAdmin && <Table.Th style={{ whiteSpace: "nowrap" }}>Email</Table.Th>}

                            <Table.Th style={{ whiteSpace: "nowrap" }}>Invitation</Table.Th>

                            {showStatus && <Table.Th style={{ whiteSpace: "nowrap" }}>Status</Table.Th>}

                            {isAdmin && (
                                <Table.Th style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                                    Action
                                </Table.Th>
                            )}
                        </Table.Tr>
                    </Table.Thead>

                    <Table.Tbody>
                        {filteredData.map((x) => {
                            const invitationUrl = getInvitationFileUrl(x.invitationLetterFile);

                            return (
                                <Table.Tr key={x.id}>
                                    <Table.Td>{x.firstName}</Table.Td>
                                    <Table.Td>{x.middleName ?? ""}</Table.Td>
                                    <Table.Td>{x.lastName}</Table.Td>
                                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                                        {formatDate(x.dateOfBirth)}
                                    </Table.Td>
                                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                                        {formatDate(x.expiryDate)}
                                    </Table.Td>
                                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                                        {formatDate(x.issueDate)}
                                    </Table.Td>
                                    <Table.Td>{x.nationality ?? ""}</Table.Td>
                                    <Table.Td>{x.passportNo ?? ""}</Table.Td>
                                    <Table.Td>{x.placeOfBirth ?? ""}</Table.Td>
                                    <Table.Td>{x.cityToApply ?? ""}</Table.Td>

                                    {isAdmin && (
                                        <Table.Td style={{ maxWidth: 220 }}>
                                            {x.email ? (
                                                <a
                                                    href={`mailto:${x.email}`}
                                                    style={{
                                                        display: "inline-block",
                                                        maxWidth: 220,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        verticalAlign: "bottom",
                                                    }}
                                                    title={x.email}
                                                >
                                                    {x.email}
                                                </a>
                                            ) : (
                                                ""
                                            )}
                                        </Table.Td>
                                    )}

                                    <Table.Td style={{ whiteSpace: "nowrap" }}>
                                        {invitationUrl ? (
                                            <Button
                                                size="xs"
                                                variant="light"
                                                component="a"
                                                href={invitationUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                View
                                            </Button>
                                        ) : (
                                            ""
                                        )}
                                    </Table.Td>

                                    {showStatus && (
                                        <Table.Td style={{ textAlign: "center" }}>
                                            <Tooltip label={x.sent ? "Sent" : "Not sent"} withArrow>
                                                <span
                                                    style={{
                                                        display: "inline-block",
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: "50%",
                                                        backgroundColor: x.sent
                                                            ? "var(--mantine-color-green-6)"
                                                            : "var(--mantine-color-gray-5)",
                                                    }}
                                                />
                                            </Tooltip>
                                        </Table.Td>
                                    )}

                                    {isAdmin && (
                                        <Table.Td style={{ textAlign: "right" }}>
                                            <Group gap={6} justify="flex-end" wrap="nowrap">
                                                <ActionIcon
                                                    variant="light"
                                                    onClick={() => onEdit?.(x)}
                                                    title="Edit"
                                                >
                                                    <IconPencil size={16} />
                                                </ActionIcon>
                                                <ActionIcon
                                                    color="red"
                                                    variant="light"
                                                    onClick={() => onDelete?.(x.id)}
                                                    title="Delete"
                                                >
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            </Group>
                                        </Table.Td>
                                    )}
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            </Table.ScrollContainer>
        </Paper>
    );
}